import { InjectionToken } from '@jkhy/frontend-utils/di';
import {
    DynamicComponentExitReason,
    DynamicComponentOptions,
} from '@jkhy/frontend-utils/lit/dynamic-component-registry';
import { Observable } from '@jkhy/frontend-utils/observable';
import { querySlot } from '@treasury/utils/functions';
import type { HTMLTemplateResult, PropertyValues } from 'lit';

export const DRAWER_CONTENT_CONTAINER_ID = 'tm-side-drawer-content';

export function getContentFromSlot(slot: HTMLSlotElement) {
    const container = querySlot(slot, `#${DRAWER_CONTENT_CONTAINER_ID}`);
    return container?.firstElementChild;
}

export enum SideDrawerButtonPosture {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
    Constructive = 'approve',
    Destructive = 'reject',
}

export interface SideDrawerButton<T = unknown> {
    label: string | (() => string);
    reason: DynamicComponentExitReason;
    posture?: SideDrawerButtonPosture;
    checkEnabled?: (data?: T) => boolean;
    onClick?: (close: (data?: T) => void, setData: (data: T) => void) => void | Promise<void>;
}

export interface SideDrawerOptions extends DynamicComponentOptions {
    align: 'left' | 'right';
    footerAlignment: 'left' | 'right';
    buttons: SideDrawerButton[];
    /** When true, shows a confirmation dialog before closing the drawer. */
    promptOnClose: boolean;
    closeOnBlur: boolean;
    width?: string;
}

const DRAWER_RESULT_EVENT = `tm-drawer-result`;

export function listenForDrawerResult<T>(element: Element, callback: (data: T) => void) {
    const listener = (e: Event) => {
        if (!(e instanceof CustomEvent)) {
            throw new Error(`Invalid event instance for "${DRAWER_RESULT_EVENT}"`);
        }

        callback((e as CustomEvent<T>).detail);
    };

    element.addEventListener(DRAWER_RESULT_EVENT, listener);

    return {
        unsubscribe: () => element.removeEventListener(DRAWER_RESULT_EVENT, listener),
    };
}

export function dispatchDrawerResult<T>(element: Element, data: T) {
    const event = new CustomEvent(DRAWER_RESULT_EVENT, {
        detail: data,
        bubbles: true,
        composed: true,
    });

    element.dispatchEvent(event);
}

// indirect reference avoids a Tailwind import leak that breaks the channel build
export interface ISideDrawerContent<T = unknown> {
    drawerButtons?: SideDrawerButton<T>[];
    drawerHeaderTemplate?: HTMLTemplateResult;
    updated$: Observable<PropertyValues>;
}

export function isSideDrawerContent<T>(element: object): element is ISideDrawerContent<T> {
    return (
        'updated$' in element &&
        element.updated$ instanceof Observable &&
        'drawerButtons' in element
    );
}

export type SideDrawerPromptFn = (
    content: string | HTMLTemplateResult,
    title: string
) => Promise<{ reason: DynamicComponentExitReason }>;
export const DrawerConfirmationPromptToken = new InjectionToken<SideDrawerPromptFn>(
    'DrawerConfirmationPrompt'
);
